import {
    SpaceBetween,
    BreadcrumbGroup,
    Header,
    Box,
    Table,
    Button,
    ButtonDropdown,
    TextFilter,
    Pagination,
    CollectionPreferences,
    Link,
} from '@amzn/awsui-components-console';
import React, { useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { fetchGroups } from './apis';
import * as Constants from './constants';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { EmptyState, getFilterCounterText } from './utils';
import { useHistory } from 'react-router-dom';
import { RegionContext } from '../../CustomContext';
import { useNotifications } from '../../NotificationContext';
import { useDrawer } from '../../DrawerContext';
import { getCurrentAuthUserEmail, isAdmin } from '../../lib/utils';

/**
 * Group manager component
 */
export default function GroupsManager() {
    const history = useHistory();
    const [selectedGroup, setSelectedGroup] = React.useState([]);
    const selectedGroupRef = useRef(selectedGroup);
    const [loading, setLoading] = React.useState(false);
    const [groups, setGroups] = React.useState([]);
    const [configureDisabled, setConfigureDisabled] = React.useState(true);
    const [manageDevicesDisabled, setManageDevicesDisabled] = React.useState(true);
    const [currentUserEmail, setCurrentUserEmail] = React.useState('');
    const [isDdfAdmin, setIsDdfAdmin] = React.useState(false);

    const { selectedRegion, setSelectedRegion } = useContext(RegionContext);
    const { addSuccess } = useNotifications();
    const { openDrawer, activeDrawerId, drawerData, updateDrawerData } = useDrawer();
    const [preferences, setPreferences] = useState({
        pageSize: 20,
        stickyColumns: { first: 1 },
        visibleContent: Constants.GROUP_VISIBLE_COLUMNS,
    });

    // Keep ref in sync with selectedGroup state
    useEffect(() => {
        selectedGroupRef.current = selectedGroup;
    }, [selectedGroup]);

    // Resolve the current user email once so we can enable Configure
    // for private group owners in addition to privilege==='admin' groups.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [email, adminResp] = await Promise.all([
                    getCurrentAuthUserEmail(),
                    isAdmin(),
                ]);
                if (!cancelled) {
                    setCurrentUserEmail(email || '');
                    setIsDdfAdmin(Boolean(adminResp?.status));
                }
            } catch (e) {
                console.error('Failed to resolve current user for Configure gating', e);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Re-evaluate Configure enablement whenever the resolved user email or the selected group changes
    useEffect(() => {
        if (selectedGroup.length === 0) {
            setConfigureDisabled(true);
            return;
        }
        const group = selectedGroup[0];
        const ownerEmail = group?.owner?.email || '';
        const isOwner = !!currentUserEmail && ownerEmail === currentUserEmail;
        setConfigureDisabled(!(isDdfAdmin || isOwner));
    }, [currentUserEmail, isDdfAdmin, selectedGroup]);

    const handleDeviceCountClick = useCallback((group) => {
        history.push({
            pathname: `/lab-management/groups/${group.id}/devices`,
            state: { groupName: group.name },
        });
    }, [history]);

    const columnDefinitions = useMemo(() => {
        return Constants.GROUP_COLUMNS_DEFINITION.map((column) => {
            if (column.id === 'deviceCount') {
                return {
                    ...column,
                    cell: (e) => {
                        const count = e.deviceCount || 0;
                        if (count > 0) {
                            return <Link onFollow={() => handleDeviceCountClick(e)}>{count}</Link>;
                        }
                        return count;
                    },
                };
            }
            return column;
        });
    }, [handleDeviceCountClick]);

    const { items, actions, collectionProps, filteredItemsCount, filterProps, paginationProps } = useCollection(
        groups,
        {
            filtering: {
                noMatch: (
                    <EmptyState
                        title="No matches"
                        subtitle="We can’t find a match."
                        action={<Button onClick={() => actions.setFiltering('')}>Clear filter</Button>}
                    />
                ),
            },
            pagination: { pageSize: preferences.pageSize },
            sorting: {
                defaultState: { sortingColumn: Constants.GROUP_SORTED_FIELD, isDescending: true },
            },
            selection: {},
        }
    );

    /**
     * process configure button click event
     */
    const onConfigureClick = () => {
        if (selectedGroup.length === 0) {
            console.error('when no group is selected, configure button should not be enabled.');
        } else {
            const group = selectedGroup[0];
            history.push({
                pathname: `/lab-management/groups/configure/${group['id']}`,
                state: group,
            });
        }
    };

    /**
     * process selection change event
     */
    const onSelectionChange = (selectedItems) => {
        // when group is selected
        setSelectedGroup(selectedItems);
        // only single selection is supported; Configure enablement is driven
        // by the useEffect above that watches selectedGroup + currentUserEmail.
        if (selectedItems.length > 0) {
            setManageDevicesDisabled(false);
            // Update delete drawer if it's open
            if (activeDrawerId === 'delete-group') {
                updateDrawerData({ group: selectedItems[0] });
            }
        } else {
            setManageDevicesDisabled(true);
            if (activeDrawerId === 'delete-group') {
                updateDrawerData({ group: null });
            }
        }
    };
    const startLoading = () => {
        // initialize loading status
        setLoading(true);
        setConfigureDisabled(true);
        setManageDevicesDisabled(true);
    };
    const endLoading = () => {
        // end loading status
        setLoading(false);
        // restore button states based on current selection using ref to avoid stale state
        onSelectionChange(selectedGroupRef.current);
    };

    /**
     * load group table
     */
    const loadTable = async () => {
        startLoading();
        try {
            const response = await fetchGroups(selectedRegion.value);
            const groups = response['response'];

            const groupsWithCounts = groups.map(group => ({
                ...group,
                thirdParty: group.thirdParty ? 'Yes' : 'No',
                deviceCount: group.devices?.length || 0,
            }));

            setGroups(groupsWithCounts);
        } catch (e) {
            console.error('Fail to load group(s) with error', e);
        }
        endLoading();
    };

    const handleManageDevices = () => {
        if (selectedGroup.length > 0) {
            history.push({
                pathname: `/lab-management/groups/${selectedGroup[0].id}/devices`,
                state: { groupName: selectedGroup[0].name },
            });
        }
    };

    const onActionsClick = ({ detail }) => {
        if (detail.id === 'create-group') {
            openDrawer('create-group', {
                onSuccess: (groupName) => {
                    loadTable();
                    addSuccess(
                        `Group ${groupName} has been created and is now available for use.`,
                        `Group ${groupName} created successfully`
                    );
                },
            });
        } else if (detail.id === 'delete' && selectedGroup.length > 0) {
            openDrawer('delete-group', {
                group: selectedGroup[0],
                onSuccess: (groupName) => {
                    setSelectedGroup([]);
                    loadTable();
                    addSuccess(
                        `The deletion of group ${groupName} has been triggered. You will receive a notification once the operation is finished.`,
                        `Delete group request triggered for ${groupName}`
                    );
                },
            });
        }
    };

    useEffect(() => {
        // initialize the component
        loadTable();
    }, []);

    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'DS2 Device Farm', href: '#' },
                    { text: 'Group management', href: '#/lab-management/groups' },
                ]}
                ariaLabel="Breadcrumbs"
            />
            <Header
                variant="h1"
                description="This page shows all DDF group(s) that you are allowed to view and configure. Configuration of common group and private group(s) created by user privilege has been disabled."
            >
                Welcome to DS2 Device Farm - Groups management
            </Header>
            <Table
                {...collectionProps}
                onSelectionChange={({ detail }) => onSelectionChange(detail.selectedItems)}
                selectedItems={selectedGroup}
                selectionType="single"
                header={
                    <Header
                        counter={`(${groups.length})`}
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button onClick={loadTable} iconName="refresh" variant="icon" />
                                <Button onClick={handleManageDevices} variant="normal" disabled={manageDevicesDisabled}>
                                    Manage Devices
                                </Button>
                                <Button disabled={configureDisabled} onClick={onConfigureClick} variant="primary">
                                    Configure
                                </Button>
                                <ButtonDropdown
                                    items={[
                                        { text: 'Create group', id: 'create-group' },
                                        { text: 'Delete', id: 'delete', disabled: selectedGroup.length === 0 },
                                    ]}
                                    onItemClick={onActionsClick}
                                >
                                    Actions
                                </ButtonDropdown>
                            </SpaceBetween>
                        }
                        description="To view detailed group's custom config, click See details button to expand."
                    >
                        DDF Groups
                    </Header>
                }
                columnDefinitions={columnDefinitions}
                visibleColumns={preferences.visibleContent}
                items={items}
                pagination={<Pagination {...paginationProps} />}
                filter={<TextFilter {...filterProps} countText={getFilterCounterText(filteredItemsCount)} />}
                empty={
                    <Box textAlign="center" color="inherit">
                        <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                            No groups to display
                        </Box>
                    </Box>
                }
                loading={loading}
                loadingText="Loading groups"
                stickyColumns={preferences.stickyColumns}
                resizableColumns
                preferences={
                    <CollectionPreferences
                        {...Constants.COLLECTION_PREFERENCES_PROPS}
                        preferences={preferences}
                        onConfirm={({ detail }) => setPreferences(detail)}
                    />
                }
            />
        </SpaceBetween>
    );
}
